import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import { useThemeTraffic, prettyPageLabel, formatDuration } from '@/hooks/useThemeTraffic';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// ============================================================
// Analytics Pro — tableau de bord analytique complet du marchand :
// trafic (visites/IP/temps), produits les plus vus, ventes et chiffre
// d'affaires. Toutes les données sont limitées à la boutique connectée.
// ============================================================

interface OrderRow { id: number; subtotal_items: number | null; status: string | null; created_at: string; currency: string; }

export default function AnalyticsProPage() {
  const { user } = useAuth();
  const { data: traffic, loading: trafficLoading } = useThemeTraffic(30);

  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [productCount, setProductCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const cid = getCommerceId(user);
      const [ordersRes, prodRes] = await Promise.all([
        supabase.from('order_headers').select('id, subtotal_items, status, created_at, currency').eq('idcommerce', cid).order('created_at', { ascending: false }).limit(500),
        supabase.from('product_items').select('id', { count: 'exact', head: true }).eq('idcommerce', cid),
      ]);
      setOrders((ordersRes.data || []) as OrderRow[]);
      setProductCount(prodRes.count || 0);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const revenue = orders
    .filter((o) => ['paid', 'processing', 'shipped', 'delivered'].includes(o.status || ''))
    .reduce((s, o) => s + (o.subtotal_items || 0), 0);
  const paidOrders = orders.filter((o) => ['paid', 'processing', 'shipped', 'delivered'].includes(o.status || '')).length;

  // Chiffre d'affaires 14 derniers jours
  const revenueSeries = (() => {
    const map: Record<string, number> = {};
    for (let i = 13; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      map[d.toISOString().split('T')[0]] = 0;
    }
    orders.forEach((o) => {
      if (!['paid', 'processing', 'shipped', 'delivered'].includes(o.status || '')) return;
      const key = (o.created_at || '').split('T')[0];
      if (map[key] !== undefined) map[key] += o.subtotal_items || 0;
    });
    return Object.entries(map).map(([date, value]) => ({
      date: new Date(date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }),
      value: Math.round(value),
    }));
  })();

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950"><i className="ri-line-chart-line mr-2 text-primary-500"></i>Analytics Pro</h2>
        <p className="text-sm text-foreground-500 mt-1">Trafic, conversion et ventes de votre boutique</p>
      </div>

      {error && <div className="mb-4 px-4 py-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
          <div className="w-9 h-9 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center mb-3"><i className="ri-money-cny-circle-line text-lg"></i></div>
          <div className="text-2xl font-bold font-heading text-foreground-950">{Math.round(revenue).toLocaleString()}</div>
          <div className="text-xs text-foreground-500 mt-1">Chiffre d’affaires</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
          <div className="w-9 h-9 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center mb-3"><i className="ri-shopping-bag-3-line text-lg"></i></div>
          <div className="text-2xl font-bold font-heading text-foreground-950">{paidOrders}</div>
          <div className="text-xs text-foreground-500 mt-1">Commandes payées</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
          <div className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center mb-3"><i className="ri-eye-line text-lg"></i></div>
          <div className="text-2xl font-bold font-heading text-foreground-950">{traffic.totalViews}</div>
          <div className="text-xs text-foreground-500 mt-1">Visites (30 j)</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
          <div className="w-9 h-9 rounded-lg bg-background-100 text-foreground-700 flex items-center justify-center mb-3"><i className="ri-group-line text-lg"></i></div>
          <div className="text-2xl font-bold font-heading text-foreground-950">{traffic.uniqueVisitors}</div>
          <div className="text-xs text-foreground-500 mt-1">Visiteurs uniques (30 j)</div>
        </div>
      </div>

      {/* CA 14 jours */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 mb-6">
        <h3 className="text-sm font-semibold text-foreground-700 mb-4 flex items-center gap-2"><i className="ri-funds-line text-accent-500"></i>Chiffre d’affaires (14 derniers jours)</h3>
        {loading ? (
          <div className="flex items-center justify-center h-56"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : (
          <div className="h-56 md:h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueSeries} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--foreground-200) / 0.4)" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'oklch(var(--foreground-500))' }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: 'oklch(var(--foreground-500))' }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ backgroundColor: 'oklch(var(--background-50))', border: '1px solid oklch(var(--foreground-200) / 0.4)', borderRadius: '8px', fontSize: '12px', color: 'oklch(var(--foreground-900))' }} />
                <Bar dataKey="value" name="CA" fill="oklch(var(--accent-500))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Pages les plus vues */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-foreground-700 mb-4 flex items-center gap-2"><i className="ri-pages-line text-primary-500"></i>Pages les plus visitées</h3>
          {trafficLoading ? <div className="flex items-center justify-center py-8"><i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i></div>
            : traffic.pages.length === 0 ? <p className="text-sm text-foreground-400 py-4 text-center">Aucune donnée</p>
            : (
              <div className="space-y-3">
                {traffic.pages.slice(0, 7).map((p) => {
                  const max = traffic.pages[0]?.views || 1;
                  return (
                    <div key={p.pageKey}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-foreground-900 truncate">{prettyPageLabel(p.pageKey, null)}</span>
                        <span className="text-xs text-foreground-500 whitespace-nowrap">{p.views} vues · {formatDuration(p.avgTime)}</span>
                      </div>
                      <div className="h-1.5 bg-background-200/70 rounded-full mt-1 overflow-hidden">
                        <div className="h-full bg-primary-400 rounded-full" style={{ width: `${(p.views / max) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </div>

        {/* Produits les plus vus */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-foreground-700 mb-4 flex items-center gap-2"><i className="ri-trophy-line text-accent-500"></i>Produits les plus consultés</h3>
          {trafficLoading ? <div className="flex items-center justify-center py-8"><i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i></div>
            : traffic.topProducts.length === 0 ? <p className="text-sm text-foreground-400 py-4 text-center">Aucune donnée</p>
            : (
              <div className="space-y-3">
                {traffic.topProducts.map((p, i) => {
                  const max = traffic.topProducts[0]?.views || 1;
                  return (
                    <div key={p.productId} className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center text-xs font-bold text-accent-700 flex-shrink-0">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm text-foreground-900 truncate">{p.name}</span>
                          <span className="text-xs text-foreground-500 whitespace-nowrap">{p.views} vues</span>
                        </div>
                        <div className="h-1.5 bg-background-200/70 rounded-full mt-1 overflow-hidden">
                          <div className="h-full bg-accent-400 rounded-full" style={{ width: `${(p.views / max) * 100}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      </div>

      <p className="text-xs text-foreground-400 mt-6"><i className="ri-information-line mr-1"></i>{productCount} produit(s) dans votre catalogue · analyses sur les 30 derniers jours.</p>
    </div>
  );
}